import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useXR } from "@react-three/xr";
import * as THREE from "three";
import { useObjectStore } from "../zustand/objects";
import { ChangePreviewScene, VRChangePreviewScene } from "./ChangePreviewScene";

function pointerIsLocked() {
  return typeof document !== "undefined" && document.pointerLockElement != null;
}

function isTeleportRayBlocker(o) {
  if (o.userData?.previewIgnoreTeleportRaycast) return false;
  if (!o.visible) return false;
  const t = o.type;
  if (t === "Mesh" || t === "InstancedMesh" || t === "SkinnedMesh") {
    if (!o.geometry) return false;
    // drei <Edges> uses LineSegments2 (extends Mesh) + LineMaterial; it must not block floor teleports.
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    if (mats.some((m) => m && m.type === "LineMaterial")) return false;
    return true;
  }
  if (t === "Sprite") return true;
  if (t === "Line" || t === "LineLoop" || t === "LineSegments" || t === "Line2") return true;
  if (t === "Points") return true;
  return false;
}

function resolveFloorTeleportHit(camera, scene, ndc, raycaster) {
  raycaster.setFromCamera(ndc, camera);
  const hits = raycaster.intersectObjects(scene.children, true);
  for (let i = 0; i < hits.length; i++) {
    const h = hits[i];
    const o = h.object;
    if (!o.visible) continue;
    if (o.userData?.previewTeleportFloor) {
      return h;
    }
    if (o.userData?.previewIgnoreTeleportRaycast) continue;
    if (isTeleportRayBlocker(o)) {
      return null;
    }
  }
  return null;
}

/**
 * Desktop preview: ring on the floor where you would teleport (sbcode.net-style).
 * Ring ray: screen center when pointer is locked, else last NDC from pointer moves **on the canvas only**.
 * (R3F `pointer` updates even when hovering HTML overlays like `#splashy`, which steels the ray to
 * screen-center and hides the ring behind scene geometry — see canvas-bound listener below.)
 * Teleport on click: same NDC as the ring (center (0,0) when pointer is locked — clientX/Y are unreliable under lock).
 * When unlocked, uses pointer-up position so edge clicks match the ring.
 * First hit wins: scene meshes block; only a clear hit on `userData.previewTeleportFloor` allows the ring + teleport.
 */
export const DesktopFloorTeleport = () => {
  const { camera, scene, gl, invalidate } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  const ndcRef = useRef(new THREE.Vector2());
  /** NDC from events on `gl.domElement` only — unchanged while hovering DOM on top of the canvas. */
  const canvasNdcRef = useRef(new THREE.Vector2(0, 0));
  const groupRef = useRef(null);
  const prevSigRef = useRef("");

  // Dashed ring geometry for dotted teleport indicator
  const ringGeometry = useMemo(() => {
    const radius = 0.34;
    const segments = 64;
    const points = [];
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * Math.PI * 2;
      points.push(Math.cos(t) * radius, 0, Math.sin(t) * radius);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
    return geometry;
  }, []);

  useEffect(() => {
    const el = gl.domElement;
    const setFromEvent = (e) => {
      const rect = el.getBoundingClientRect();
      const w = rect.width || 1;
      const h = rect.height || 1;
      canvasNdcRef.current.x = ((e.clientX - rect.left) / w) * 2 - 1;
      canvasNdcRef.current.y = -((e.clientY - rect.top) / h) * 2 + 1;
    };
    const onMove = (e) => setFromEvent(e);
    const onEnter = (e) => setFromEvent(e);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerenter", onEnter);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerenter", onEnter);
    };
  }, [gl]);

  useFrame(() => {
    const locked = pointerIsLocked();
    const ndc = ndcRef.current;
    if (locked) {
      ndc.set(0, 0);
    } else {
      ndc.copy(canvasNdcRef.current);
    }

    const validHit = resolveFloorTeleportHit(camera, scene, ndc, raycaster.current);

    const sig = validHit
      ? `${validHit.point.x.toFixed(3)},${validHit.point.z.toFixed(3)}`
      : "none";
    const camSig = `${camera.rotation.x.toFixed(4)},${camera.rotation.y.toFixed(4)}`;
    const frameSig = locked
      ? `${sig}|${camSig}`
      : `${sig}|${canvasNdcRef.current.x.toFixed(4)},${canvasNdcRef.current.y.toFixed(4)}`;
    if (frameSig !== prevSigRef.current) {
      prevSigRef.current = frameSig;
      invalidate();
    }

    if (groupRef.current) {
      groupRef.current.visible = !!validHit;
      if (validHit) {
        groupRef.current.position.set(validHit.point.x, 0.04, validHit.point.z);
      }
    }
  });

  useEffect(() => {
    const el = gl.domElement;
    const onPointerUp = (e) => {
      if (e.button !== 0) return;
      const clickNdc = new THREE.Vector2();
      if (pointerIsLocked()) {
        clickNdc.set(0, 0);
      } else {
        const rect = el.getBoundingClientRect();
        clickNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        clickNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      }
      const h = resolveFloorTeleportHit(camera, scene, clickNdc, raycaster.current);
      if (!h?.object?.userData?.previewTeleportFloor) return;
      const st = useObjectStore.getState();
      st.setTeleportCount(st.teleportCount + 1);
      camera.position.x = h.point.x;
      camera.position.z = h.point.z;
      st.updatePreviewCamera("position", [
        camera.position.x,
        camera.position.y,
        camera.position.z,
      ]);
      invalidate();
    };
    el.addEventListener("pointerup", onPointerUp);
    return () => el.removeEventListener("pointerup", onPointerUp);
  }, [gl, camera, scene, invalidate]);

  return (
    <group ref={groupRef} visible={false} userData={{ previewIgnoreTeleportRaycast: true }}>
      <line
        geometry={ringGeometry}
        userData={{ previewIgnoreTeleportRaycast: true }}
        raycast={() => null}
      >
        <lineDashedMaterial
          color="rgb(250, 199, 16)"
          dashSize={0.08}
          gapSize={0.12}
          scale={1}
          linewidth={3}
          transparent
          opacity={0.95}
          depthTest={false}
          toneMapped={false}
        />
      </line>
    </group>
  );
};

export const ChangeSceneDesktop = () => {

    const {
        setTeleportCount,
        teleportCount,
        updatePreviewCamera,
        previewCameraPosition
    } = useObjectStore();

    const { camera } = useThree()

    return (<>
        <ChangePreviewScene position={[camera.position.x + 15, 10, camera.position.z]}
            rotation={[0, - Math.PI / 2, 0]} />
        <ChangePreviewScene position={[camera.position.x - 15, 10, camera.position.z]}
            rotation={[0, Math.PI / 2, 0]} />
        <ChangePreviewScene position={[camera.position.x, 10, camera.position.z - 15]}
            rotation={[0, 0, 0]} />
        <ChangePreviewScene position={[camera.position.x, 10, camera.position.z + 15]}
            rotation={[0, Math.PI, 0]} />
    </>
    )


}


export const VRTeleportFloorGrid = () => {


    const {
        setTeleportCount,
        teleportCount,
        updatePreviewCamera
    } = useObjectStore();



    const [hovered, setHovered] = useState('')

    const { player } = useXR()

    const teleportPosition = (val) => {
        setTeleportCount(teleportCount + 1)
        player.position.x = val[0]
        player.position.z = val[2]
        updatePreviewCamera('position', val)
    }

    const positionArray = [
        [player.position.x - 5, 0, player.position.z - 5],
        [player.position.x - 5, 0, player.position.z - 4],
        [player.position.x - 5, 0, player.position.z - 3],
        [player.position.x - 5, 0, player.position.z - 2],
        [player.position.x - 5, 0, player.position.z - 1],
        [player.position.x - 5, 0, player.position.z],
        [player.position.x - 5, 0, player.position.z + 1],
        [player.position.x - 5, 0, player.position.z + 2],
        [player.position.x - 5, 0, player.position.z + 3],
        [player.position.x - 5, 0, player.position.z + 4],
        [player.position.x - 5, 0, player.position.z + 5],

        [player.position.x - 4, 0, player.position.z - 5],
        [player.position.x - 4, 0, player.position.z - 4],
        [player.position.x - 4, 0, player.position.z - 3],
        [player.position.x - 4, 0, player.position.z - 2],
        [player.position.x - 4, 0, player.position.z - 1],
        [player.position.x - 4, 0, player.position.z],
        [player.position.x - 4, 0, player.position.z + 1],
        [player.position.x - 4, 0, player.position.z + 2],
        [player.position.x - 4, 0, player.position.z + 3],
        [player.position.x - 4, 0, player.position.z + 4],
        [player.position.x - 4, 0, player.position.z + 5],

        [player.position.x - 3, 0, player.position.z - 5],
        [player.position.x - 3, 0, player.position.z - 4],
        [player.position.x - 3, 0, player.position.z - 3],
        [player.position.x - 3, 0, player.position.z - 2],
        [player.position.x - 3, 0, player.position.z - 1],
        [player.position.x - 3, 0, player.position.z],
        [player.position.x - 3, 0, player.position.z + 1],
        [player.position.x - 3, 0, player.position.z + 2],
        [player.position.x - 3, 0, player.position.z + 3],
        [player.position.x - 3, 0, player.position.z + 4],
        [player.position.x - 3, 0, player.position.z + 5],

        [player.position.x - 2, 0, player.position.z - 5],
        [player.position.x - 2, 0, player.position.z - 4],
        [player.position.x - 2, 0, player.position.z - 3],
        [player.position.x - 2, 0, player.position.z - 2],
        [player.position.x - 2, 0, player.position.z - 1],
        [player.position.x - 2, 0, player.position.z],
        [player.position.x - 2, 0, player.position.z + 1],
        [player.position.x - 2, 0, player.position.z + 2],
        [player.position.x - 2, 0, player.position.z + 3],
        [player.position.x - 2, 0, player.position.z + 4],
        [player.position.x - 2, 0, player.position.z + 5],

        [player.position.x - 1, 0, player.position.z - 5],
        [player.position.x - 1, 0, player.position.z - 4],
        [player.position.x - 1, 0, player.position.z - 3],
        [player.position.x - 1, 0, player.position.z - 2],
        [player.position.x - 1, 0, player.position.z - 1],
        [player.position.x - 1, 0, player.position.z],
        [player.position.x - 1, 0, player.position.z + 1],
        [player.position.x - 1, 0, player.position.z + 2],
        [player.position.x - 1, 0, player.position.z + 3],
        [player.position.x - 1, 0, player.position.z + 4],
        [player.position.x - 1, 0, player.position.z + 5],

        [player.position.x, 0, player.position.z - 5],
        [player.position.x, 0, player.position.z - 4],
        [player.position.x, 0, player.position.z - 3],
        [player.position.x, 0, player.position.z - 2],
        [player.position.x, 0, player.position.z - 1],
        // this is where the user's player is
        [player.position.x, 0, player.position.z + 1],
        [player.position.x, 0, player.position.z + 2],
        [player.position.x, 0, player.position.z + 3],
        [player.position.x, 0, player.position.z + 4],
        [player.position.x, 0, player.position.z + 5],

        [player.position.x + 1, 0, player.position.z - 5],
        [player.position.x + 1, 0, player.position.z - 4],
        [player.position.x + 1, 0, player.position.z - 3],
        [player.position.x + 1, 0, player.position.z - 2],
        [player.position.x + 1, 0, player.position.z - 1],
        [player.position.x + 1, 0, player.position.z],
        [player.position.x + 1, 0, player.position.z + 1],
        [player.position.x + 1, 0, player.position.z + 2],
        [player.position.x + 1, 0, player.position.z + 3],
        [player.position.x + 1, 0, player.position.z + 4],
        [player.position.x + 1, 0, player.position.z + 5],

        [player.position.x + 2, 0, player.position.z - 5],
        [player.position.x + 2, 0, player.position.z - 4],
        [player.position.x + 2, 0, player.position.z - 3],
        [player.position.x + 2, 0, player.position.z - 2],
        [player.position.x + 2, 0, player.position.z - 1],
        [player.position.x + 2, 0, player.position.z],
        [player.position.x + 2, 0, player.position.z + 1],
        [player.position.x + 2, 0, player.position.z + 2],
        [player.position.x + 2, 0, player.position.z + 3],
        [player.position.x + 2, 0, player.position.z + 4],
        [player.position.x + 2, 0, player.position.z + 5],

        [player.position.x + 3, 0, player.position.z - 5],
        [player.position.x + 3, 0, player.position.z - 4],
        [player.position.x + 3, 0, player.position.z - 3],
        [player.position.x + 3, 0, player.position.z - 2],
        [player.position.x + 3, 0, player.position.z - 1],
        [player.position.x + 3, 0, player.position.z],
        [player.position.x + 3, 0, player.position.z + 1],
        [player.position.x + 3, 0, player.position.z + 2],
        [player.position.x + 3, 0, player.position.z + 3],
        [player.position.x + 3, 0, player.position.z + 4],
        [player.position.x + 3, 0, player.position.z + 5],

        [player.position.x + 4, 0, player.position.z - 5],
        [player.position.x + 4, 0, player.position.z - 4],
        [player.position.x + 4, 0, player.position.z - 3],
        [player.position.x + 4, 0, player.position.z - 2],
        [player.position.x + 4, 0, player.position.z - 1],
        [player.position.x + 4, 0, player.position.z],
        [player.position.x + 4, 0, player.position.z + 1],
        [player.position.x + 4, 0, player.position.z + 2],
        [player.position.x + 4, 0, player.position.z + 3],
        [player.position.x + 4, 0, player.position.z + 4],
        [player.position.x + 4, 0, player.position.z + 5],

        [player.position.x + 5, 0, player.position.z - 5],
        [player.position.x + 5, 0, player.position.z - 4],
        [player.position.x + 5, 0, player.position.z - 3],
        [player.position.x + 5, 0, player.position.z - 2],
        [player.position.x + 5, 0, player.position.z - 1],
        [player.position.x + 5, 0, player.position.z],
        [player.position.x + 5, 0, player.position.z + 1],
        [player.position.x + 5, 0, player.position.z + 2],
        [player.position.x + 5, 0, player.position.z + 3],
        [player.position.x + 5, 0, player.position.z + 4],
        [player.position.x + 5, 0, player.position.z + 5]

    ]
 
    // return (
    //     positionArray.map((mapped, i) => {
    //         return (
    //             <Interactive
    //                 key={i}
    //                 onHover={() => setHovered(i)}
    //                 onBlur={() => setHovered('')}
    //                 onSelectEnd={() => teleportPosition(mapped)}
    //             >
    //                 <Box
    //                     position={mapped}
    //                     args={[0.75, 0.1, 0.75]}
    //                 >
    //                     <meshBasicMaterial
    //                         color={hovered === i ? "rgb(250, 199, 16)" : "white"}
    //                     />
    //                 </Box>
    //             </Interactive>)

            return (<>
                <VRChangePreviewScene position={[player.position.x + 15, 10, player.position.z]}
                    rotation={[0, - Math.PI / 2, 0]} />
                <VRChangePreviewScene position={[player.position.x - 15, 10, player.position.z]}
                    rotation={[0, Math.PI / 2, 0]} />
                <VRChangePreviewScene position={[player.position.x, 10, player.position.z - 15]}
                    rotation={[0, 0, 0]} />
                <VRChangePreviewScene position={[player.position.x, 10, player.position.z + 15]}
                    rotation={[0, Math.PI, 0]} />
                {/* {positionArray.map((mapped, i) => {
                    return (<Interactive
                        key={i}
                        onHover={() => setHovered(i)}
                        onBlur={() => setHovered('')}
                        onSelectEnd={() => teleportPosition(mapped)}
                    >
                        <Box
                            position={mapped}
                            args={[0.75, 0.1, 0.75]}
                        >
                            <meshBasicMaterial
                                color={hovered === i ? "rgb(250, 199, 16)" : "white"}
                            />
                        </Box>
                    </Interactive>)
        })} */}
    </>
    )


}